import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import get from 'lodash/get';
import first from 'lodash/first';
import VibeTooltip from '../VibeTooltip/VibeTooltip';
import VibeIcon from '../VibeIcon/VibeIcon';
import {
  getDayOfWeekIndex,
  convertDuration,
} from '../../utils/TimeUtil';
import {
  transparent,
} from '../../utils/ColorUtil';
import {
  isMessageApplicable,
} from '../../helpers/Location';
import {
  setDisplay,
} from '../../actions/Display/DisplayActions';
import viPlayCircleOutline from '../../icons/viPlayCircleOutline';
import viCalendar from '../../icons/viCalendar';
import * as color from '../../sass/color.scss';
import './LoopListItem.scss';

class LoopListItem extends PureComponent {
  constructor(props) {
    super(props);

    const {
      companyId,
      locationId,
      message: {
        startDate,
        stopDate,
        locations,
      },
      loopLocations = [],
    } = props;

    let startDateFormatted = null;
    let stopDateFormatted = null;

    const willPlay = isMessageApplicable({
      companyId,
      locationId,
      locations,
      parentLocations: loopLocations,
    });

    if (startDate || stopDate) {
      const startAt = startDate
        ? new moment(startDate).startOf('day')
        : null;
      const stopAt = stopDate
        ? new moment(stopDate).endOf('day')
        : null;

      if (startAt) {
        startDateFormatted = startAt.format('MM/DD/YYYY');
      }

      if (stopAt) {
        stopDateFormatted = stopAt.format('MM/DD/YYYY');
      }
    }

    this.state = {
      startDateFormatted,
      stopDateFormatted,
      willPlay,
    };
  }

  /**
   * Open the Display Player to preview the message
   */
  openDisplayPlayer = () => {
    const {
      message,
      currentLocation,
      setDisplay,
    } = this.props;

    const resolution = get(currentLocation, 'resolution.size')?.join(',');
    const orientation = get(currentLocation, 'orientation');

    setDisplay({
      show: true,
      messages: [message],
      isLoop: false,
      resolution,
      orientation,
    });
  };

  /**
   * Render the Tooltip when the message hasn't started or is expired
   */
  renderTooltip = () => {
    const {
      message: {
        name,
        started,
        expired,
        daysOfWeek,
      },
    } = this.props;

    const {
      willPlay,
      startDateFormatted,
      stopDateFormatted,
    } = this.state;

    const messages = [(
      <div key="message-name">
        {name}
      </div>
    )];

    if (!started) {
      messages.push(
        <div key="message-play-date">
          will begin playing on {startDateFormatted}.
        </div>,
      );
    }

    if (expired) {
      messages.push(
        <div key="message-expired">
          Expired on {stopDateFormatted}. Will be
          <br />automatically removed from all
          <br />loops by tomorrow.
        </div>,
      );
    }

    if (!willPlay) {
      messages.push(
        <div key="message-not-playable">
          Not playable at this location
        </div>,
      );
    }

    if (daysOfWeek.length > 0 && daysOfWeek.length < 7) {
      messages.push(
        <div key="message-not-today">
          This message is only scheduled to play on<br />
          {daysOfWeek.join(', ')}
        </div>,
      );
    }

    return (
      <div
        style={{
          textAlign: 'center',
          lineHeight: 1.67,
        }}
      >
        {messages}
      </div>
    );
  };

  render() {
    const {
      className,
      message,
      day,
    } = this.props;

    const {
      willPlay,
    } = this.state;

    const {
      name,
      daysOfWeek,
      started,
      expired,
      media,
    } = message;

    // use the first media item for calculating duration
    const mediaItem = first(media) || {};

    // message is scheduled for specific days of the week (if all days are selected treat as not scheduled)
    const scheduled = daysOfWeek.length > 0 && daysOfWeek.length < 7;

    const playControlColor = expired
      // message is expired
      ? color.error
      : color.success;

    const dayIndex = getDayOfWeekIndex(day);
    const daysOfWeekIndexes = daysOfWeek.map(day => getDayOfWeekIndex(day));

    return (
      <div className={classNames('LoopListItem', className, {
        'not-started': !started,
        expired,
        scheduled,
        'not-today': scheduled && day && !daysOfWeekIndexes.includes(dayIndex),
        'not-playable': !willPlay,
      })}
      >
        <VibeIcon
          className="play-message"
          icon={viPlayCircleOutline}
          color={playControlColor}
          hoverColor={transparent(playControlColor, 75)}
          size={16}
          tooltip={`Play Message (${convertDuration(mediaItem.durationSeconds, 'm:ss')})`}
          tooltipProps={{
            placement: 'top',
          }}
          onClick={this.openDisplayPlayer}
        />

        <VibeTooltip
          title={this.renderTooltip()}
          placement="top"
          enterDelay={500}
        >
          <div
            style={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              overflow: 'hidden',
            }}
          >
            <div className="message-name">
              {message.name}
            </div>

            {daysOfWeek.length > 0 && daysOfWeek.length < 7 && (
              <VibeIcon
                className="calendar-icon"
                icon={viCalendar}
                color={color.primary}
                size={16}
              />
            )}
          </div>
        </VibeTooltip>
      </div>

    );
  }
}

LoopListItem.propTypes = {
  className: PropTypes.string,
  /** Message */
  message: PropTypes.object.isRequired,
  /** Company ID of Message Block */
  companyId: PropTypes.string,
  /** Location ID of Message Block */
  locationId: PropTypes.string,
  /** Day of the week the message block is being shown */
  day: PropTypes.string,
};

LoopListItem.defaultProps = {
  className: '',
  companyId: null,
  locationId: null,
  day: null,
};

function mapStateToProps(state) {
  return {
    currentLocation: state.locations.currentLocation,
  };
}

const mapDispatchToProps = {
  setDisplay,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoopListItem);
